<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import conversationAPI from '../../../../api/inbox/conversation';
import KanbanAPI from '../../../../api/kanban';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  currentFunnel: {
    type: Object,
    default: null,
  },
  stageId: {
    type: [String, Number],
    default: null,
  },
});
const emit = defineEmits(['close', 'items-imported']);
const { t } = useI18n();
const store = useStore();

const conversations = ref([]);
const loadingConversations = ref(false);
const conversationSearch = ref('');
const selectedConversations = ref([]);
const creatingItems = ref(false);

const selectedFunnel = computed(
  () => props.currentFunnel || store.getters['funnel/getSelectedFunnel']
);

const availableStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];
  return Object.entries(selectedFunnel.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);
});

const firstStage = computed(() => {
  if (availableStages.value.length === 0) return null;
  return availableStages.value[0].id;
});

const targetStage = computed(() => {
  if (!props.stageId) return firstStage.value;
  const stageId = String(props.stageId);
  const hasStage = availableStages.value.some(
    stage => String(stage.id) === stageId
  );
  return hasStage ? stageId : firstStage.value;
});

const filteredConversations = computed(() => {
  if (!conversationSearch.value) return conversations.value;
  return conversations.value.filter(c => {
    const sender = c.meta?.sender;
    const text = `${c.id} ${sender?.name || ''} ${
      sender?.email || ''
    }`.toLowerCase();
    return text.includes(conversationSearch.value.toLowerCase());
  });
});

const isConversationSelected = conversationId => {
  return selectedConversations.value.includes(conversationId);
};

const toggleConversation = conversationId => {
  const index = selectedConversations.value.indexOf(conversationId);
  if (index > -1) {
    selectedConversations.value.splice(index, 1);
  } else {
    selectedConversations.value.push(conversationId);
  }
};

const selectAll = () => {
  if (
    selectedConversations.value.length === filteredConversations.value.length
  ) {
    selectedConversations.value = [];
  } else {
    selectedConversations.value = filteredConversations.value.map(c => c.id);
  }
};

const fetchConversations = async () => {
  try {
    loadingConversations.value = true;
    const response = await conversationAPI.get({});
    conversations.value = response.data.data.payload || [];
  } catch (error) {
    console.error('Erro ao buscar conversas:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao buscar conversas',
      type: 'error',
    });
  } finally {
    loadingConversations.value = false;
  }
};

const createItemsFromConversations = async () => {
  if (!selectedFunnel.value?.id) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.ERRORS.NO_FUNNEL_SELECTED'),
      type: 'error',
    });
    return;
  }

  if (selectedConversations.value.length === 0) {
    emitter.emit('newToastMessage', {
      message: 'Selecione pelo menos uma conversa',
      type: 'error',
    });
    return;
  }

  if (!targetStage.value) {
    emitter.emit('newToastMessage', {
      message: 'Nenhuma etapa disponível no funil',
      type: 'error',
    });
    return;
  }

  try {
    creatingItems.value = true;
    const createdItems = [];
    const errors = [];

    for (let index = 0; index < selectedConversations.value.length; index++) {
      const conversationId = selectedConversations.value[index];
      const conversation = conversations.value.find(
        c => c.id === conversationId
      );

      if (!conversation) {
        errors.push(`Conversa ${conversationId} não encontrada`);
        continue;
      }

      const sender = conversation.meta?.sender;
      const title =
        sender?.name || sender?.email || `Conversa #${conversation.id}`;

      // Usar last_non_activity_message se disponível, senão usar a primeira mensagem não-ativa
      let description = '';
      if (conversation.last_non_activity_message?.content) {
        description = conversation.last_non_activity_message.content;
      } else if (conversation.messages?.length > 0) {
        // Buscar primeira mensagem que não seja de atividade (message_type !== 2)
        const nonActivityMessage = conversation.messages.find(
          msg => msg.message_type !== 2 && msg.content
        );
        description = nonActivityMessage?.content || '';
      }

      // Montar item_details seguindo o padrão do KanbanItemForm
      const itemDetails = {
        title,
        description,
        priority: conversation.priority || 'medium',
        conversation_id: conversation.id,
        custom_attributes: [],
      };

      // Criar o payload completo seguindo o padrão do KanbanItemForm
      const itemPayload = {
        funnel_id: selectedFunnel.value.id,
        funnel_stage: targetStage.value,
        position: index,
        assigned_agents: conversation.meta?.assignee?.id
          ? [conversation.meta.assignee.id]
          : [],
        item_details: itemDetails,
        conversation_display_id: conversation.id,
      };

      try {
        const response = await KanbanAPI.createItem(itemPayload);
        const createdItem = response.data;
        createdItems.push(createdItem);

        // Emitir evento para notificar criação de item (igual ao KanbanTab)
        emitter.emit('kanbanItemCreated', createdItem);
      } catch (error) {
        console.error(
          `Erro ao criar item para conversa ${conversationId}:`,
          error
        );
        errors.push(`Erro ao criar item para conversa #${conversationId}`);
      }
    }

    if (createdItems.length > 0) {
      emitter.emit('newToastMessage', {
        message: `${createdItems.length} item${createdItems.length !== 1 ? 's' : ''} criado${createdItems.length !== 1 ? 's' : ''} com sucesso${errors.length > 0 ? ` (${errors.length} erro${errors.length !== 1 ? 's' : ''})` : ''}`,
        type: errors.length > 0 ? 'warning' : 'success',
      });

      emit('items-imported', {
        created_count: createdItems.length,
        error_count: errors.length,
      });

      emit('close');
    } else {
      emitter.emit('newToastMessage', {
        message: 'Nenhum item foi criado',
        type: 'error',
      });
    }
  } catch (error) {
    console.error('Erro ao criar itens:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao criar itens',
      type: 'error',
    });
  } finally {
    creatingItems.value = false;
  }
};

onMounted(() => {
  if (props.show) {
    fetchConversations();
  }
});

watch(
  () => props.show,
  newValue => {
    if (newValue) {
      fetchConversations();
      selectedConversations.value = [];
      conversationSearch.value = '';
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="emit('close')">
      <div class="modal-container">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-extrabold">Importar Conversas</h3>
            <button
              type="button"
              class="p-1 hover:bg-n-alpha-2 rounded transition-colors"
              @click="emit('close')"
            >
              <span class="i-ri-close-line size-5" />
            </button>
          </div>

          <div class="space-y-4">
            <!-- Busca -->
            <div class="relative">
              <input
                v-model="conversationSearch"
                type="text"
                class="w-full px-4 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-xl focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                placeholder="Buscar conversas..."
              />
              <span
                class="i-ri-search-line size-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-n-slate-10"
              />
            </div>

            <!-- Selecionar todos -->
            <div class="flex items-center justify-between">
              <span class="text-sm text-n-slate-11">
                {{ selectedConversations.length }} de
                {{ filteredConversations.length }} selecionada{{
                  selectedConversations.length !== 1 ? 's' : ''
                }}
              </span>
              <button
                type="button"
                class="text-sm text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300"
                @click="selectAll"
              >
                {{
                  selectedConversations.length === filteredConversations.length
                    ? 'Desmarcar todas'
                    : 'Selecionar todas'
                }}
              </button>
            </div>

            <!-- Lista de conversas -->
            <div
              class="border border-n-weak rounded-xl max-h-96 overflow-y-auto"
            >
              <div
                v-if="loadingConversations"
                class="flex justify-center items-center py-8"
              >
                <span class="loading-spinner" />
              </div>

              <div
                v-else-if="filteredConversations.length === 0"
                class="flex flex-col items-center justify-center py-8 text-n-slate-11"
              >
                <span class="i-ri-chat-1-line size-8 mb-2 text-n-slate-10" />
                <p class="text-sm">
                  {{
                    conversationSearch
                      ? 'Nenhuma conversa encontrada'
                      : 'Nenhuma conversa disponível'
                  }}
                </p>
              </div>

              <div v-else class="divide-y divide-n-weak">
                <div
                  v-for="conversation in filteredConversations"
                  :key="conversation.id"
                  class="p-4 hover:bg-n-alpha-2 cursor-pointer transition-colors"
                  @click="toggleConversation(conversation.id)"
                >
                  <div class="flex items-start gap-3">
                    <input
                      type="checkbox"
                      :checked="isConversationSelected(conversation.id)"
                      class="mt-1 w-4 h-4 text-woot-500 border-n-weak rounded focus:ring-woot-500"
                      @click.stop
                      @change="toggleConversation(conversation.id)"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="text-sm font-medium text-n-slate-12">
                          #{{ conversation.id }}
                        </span>
                        <span class="text-xs text-n-slate-11">
                          {{
                            conversation.meta?.sender?.name ||
                            conversation.meta?.sender?.email ||
                            'Sem contato'
                          }}
                        </span>
                      </div>
                      <p
                        v-if="conversation.messages?.[0]?.content"
                        class="text-sm text-n-slate-11 truncate"
                      >
                        {{ conversation.messages[0].content }}
                      </p>
                      <p v-else class="text-xs text-n-slate-10 italic">
                        Sem mensagens
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botões -->
          <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-n-weak">
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="sm"
              @click="emit('close')"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="solid"
              color="blue"
              size="sm"
              :is-loading="creatingItems"
              :disabled="
                selectedConversations.length === 0 || !selectedFunnel?.id
              "
              @click="createItemsFromConversations"
            >
              Importar
              {{
                selectedConversations.length > 0
                  ? `(${selectedConversations.length})`
                  : ''
              }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.modal-overlay {
  @apply fixed inset-0 z-[9999] flex items-center justify-center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-container {
  @apply bg-n-solid-2 rounded-xl shadow-xl;
  @apply max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto;
  animation: modalFadeIn 0.2s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.loading-spinner {
  @apply w-6 h-6 border-2 border-n-weak border-t-woot-500 rounded-full animate-spin;
}
</style>
